// components/onboarding/OnboardingOverlay.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import '../../styles/Onboarding.css';

// Mobile breakpoint - matches typical mobile sidebar breakpoint
const MOBILE_BREAKPOINT = 768;

/**
 * OnboardingOverlay
 *
 * Creates a dark overlay with a spotlight cutout around the target element.
 * Uses SVG mask for the spotlight effect.
 * Handles both desktop and mobile layouts.
 */
const OnboardingOverlay = () => {
    const {
        isOnboardingActive,
        currentStep,
        getCurrentStepConfig,
    } = useOnboarding();

    const [targetRect, setTargetRect] = useState(null);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

    const stepConfig = getCurrentStepConfig();

    /**
     * Check if mobile
     */
    const checkMobile = useCallback(() => {
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    }, []);

    /**
     * Update target element position
     */
    const updateTargetPosition = useCallback(() => {
        if (!stepConfig?.targetSelector) {
            setTargetRect(null);
            return;
        }

        // On mobile, look for the element in the mobile sidebar first
        let targetElement = null;
        if (isMobile) {
            const mobileSidebar = document.querySelector('.mobile-sidebar');
            if (mobileSidebar) {
                targetElement = mobileSidebar.querySelector(stepConfig.targetSelector);
            }
        }

        // Fall back to document-level query
        if (!targetElement) {
            targetElement = document.querySelector(stepConfig.targetSelector);
        }

        if (!targetElement) {
            setTargetRect(null);
            return;
        }

        const rect = targetElement.getBoundingClientRect();
        // Add some padding around the highlighted element
        const padding = 8;
        setTargetRect({
            x: rect.left - padding,
            y: rect.top - padding,
            width: rect.width + (padding * 2),
            height: rect.height + (padding * 2),
            borderRadius: 12,
        });
    }, [stepConfig, isMobile]);

    /**
     * Update window size
     */
    const updateWindowSize = useCallback(() => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    }, []);

    // Handle window resize for mobile detection
    useEffect(() => {
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [checkMobile]);

    // Update positions on step change and window events
    useEffect(() => {
        if (!isOnboardingActive) return;

        // On mobile, wait for sidebar to open before updating position
        const delay = isMobile ? 350 : 100;
        const timer = setTimeout(() => {
            updateTargetPosition();
            updateWindowSize();
        }, delay);

        // Event listeners
        window.addEventListener('resize', updateTargetPosition);
        window.addEventListener('resize', updateWindowSize);
        window.addEventListener('scroll', updateTargetPosition);

        // MutationObserver for DOM changes
        const observer = new MutationObserver(() => {
            updateTargetPosition();
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        });

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateTargetPosition);
            window.removeEventListener('resize', updateWindowSize);
            window.removeEventListener('scroll', updateTargetPosition);
            observer.disconnect();
        };
    }, [isOnboardingActive, currentStep, isMobile, updateTargetPosition, updateWindowSize]);

    // Don't render overlay for welcome modal (step 0)
    if (!isOnboardingActive || currentStep === 0) {
        return null;
    }

    return (
        <div className="onboarding-overlay">
            <svg
                className="onboarding-overlay-svg"
                width={windowSize.width}
                height={windowSize.height}
            >
                <defs>
                    <mask id="onboarding-spotlight-mask">
                        {/* White = visible, Black = hidden */}
                        <rect
                            x="0"
                            y="0"
                            width={windowSize.width}
                            height={windowSize.height}
                            fill="white"
                        />
                        {/* Cutout for the target element */}
                        {targetRect && (
                            <rect
                                x={targetRect.x}
                                y={targetRect.y}
                                width={targetRect.width}
                                height={targetRect.height}
                                rx={targetRect.borderRadius}
                                ry={targetRect.borderRadius}
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>

                {/* Overlay with mask applied */}
                <rect
                    x="0"
                    y="0"
                    width={windowSize.width}
                    height={windowSize.height}
                    fill="rgba(0, 0, 0, 0.6)"
                    mask="url(#onboarding-spotlight-mask)"
                />
            </svg>

            {/* Highlight border around target */}
            {targetRect && (
                <div
                    className="onboarding-highlight-ring"
                    style={{
                        top: targetRect.y,
                        left: targetRect.x,
                        width: targetRect.width,
                        height: targetRect.height,
                        borderRadius: targetRect.borderRadius,
                    }}
                />
            )}
        </div>
    );
};

export default OnboardingOverlay;
